import { produce } from 'immer'

// =============================================================================
// Redux slice for the voice-agents page: AI-agent-user list, agent detail,
// per-node/variable/tool version history, and drawer UI state. Prompt,
// variable, and tool content are editable with version history + revert + reset.
// =============================================================================

// Action Types
export const SET_LOADING = 'voiceAgents/setLoading'
export const SET_ERROR = 'voiceAgents/setError'
export const SET_AI_AGENT_USERS = 'voiceAgents/setAiAgentUsers'
export const SET_AGENT_DETAIL = 'voiceAgents/setAgentDetail'
export const SET_AGENT_CONFIG_MISSING = 'voiceAgents/setAgentConfigMissing'
export const SET_AGENT_NOT_FOUND = 'voiceAgents/setAgentNotFound'
export const SET_NODE = 'voiceAgents/setNode'
export const SET_VARIABLES = 'voiceAgents/setVariables'
// Switching the active config generation swaps which overrides exist, so every
// per-target version cache has to go with it.
export const CLEAR_VERSION_CACHES = 'voiceAgents/clearVersionCaches'
export const SET_VERSIONS = 'voiceAgents/setVersions'
export const SET_VARIABLE_VERSIONS = 'voiceAgents/setVariableVersions'
export const SET_TOOL_VERSIONS = 'voiceAgents/setToolVersions'
export const SET_YAML = 'voiceAgents/setYaml'
export const SET_SELECTED_AGENT = 'voiceAgents/setSelectedAgent'
export const SET_SELECTED_NODE = 'voiceAgents/setSelectedNode'
export const SET_DRAWER_ACTIVE_TAB = 'voiceAgents/setDrawerActiveTab'
export const SET_DRAWER_VERSION_OVERRIDE = 'voiceAgents/setDrawerVersionOverride'
export const CLEAR_DRAWER_VERSION_OVERRIDE = 'voiceAgents/clearDrawerVersionOverride'
export const SET_DRAWER_HISTORY_OPEN = 'voiceAgents/setDrawerHistoryOpen'
export const SET_CHANGE_REQUESTS = 'voiceAgents/setChangeRequests'
export const UPSERT_CHANGE_REQUEST = 'voiceAgents/upsertChangeRequest'
export const SET_TRANSCRIPT_CHANGE_REQUESTS = 'voiceAgents/setTranscriptChangeRequests'
export const UPSERT_TRANSCRIPT_CHANGE_REQUEST = 'voiceAgents/upsertTranscriptChangeRequest'
export const SET_FOCUSED_TRANSCRIPT_CHANGE_REQUEST = 'voiceAgents/setFocusedTranscriptChangeRequest'
export const SET_DRAWER_CONTENT_VIEW = 'voiceAgents/setDrawerContentView'
export const SET_FOCUSED_CHANGE_REQUEST = 'voiceAgents/setFocusedChangeRequest'
export const FOCUS_CHANGE_REQUEST = 'voiceAgents/focusChangeRequest'
export const FOCUS_CHANGE_REQUEST_IN_CONTENT = 'voiceAgents/focusChangeRequestInContent'
export const SET_VOICES = 'voiceAgents/setVoices'
export const SET_CHOSEN_VOICE = 'voiceAgents/setChosenVoice'
export const SET_SAVED_VOICE = 'voiceAgents/setSavedVoice'
export const SET_LLM_CATALOG = 'voiceAgents/setLlmCatalog'
export const SET_LLM_SETTINGS = 'voiceAgents/setLlmSettings'
export const SET_LLM_VERSIONS = 'voiceAgents/setLlmVersions'
export const SET_LANGUAGE_CATALOG = 'voiceAgents/setLanguageCatalog'
export const SET_LANGUAGE_SETTINGS = 'voiceAgents/setLanguageSettings'
export const SET_LANGUAGE_VERSIONS = 'voiceAgents/setLanguageVersions'
export const SET_LANGUAGE_PREVIEW = 'voiceAgents/setLanguagePreview'
export const SET_CALL_DATA_DELETION = 'voiceAgents/setCallDataDeletion'

// Action Creators
export const setLoading = (payload) => ({ type: SET_LOADING, payload })
export const setError = (payload) => ({ type: SET_ERROR, payload })
export const setAiAgentUsers = (payload) => ({ type: SET_AI_AGENT_USERS, payload })
export const setAgentDetail = (payload) => ({ type: SET_AGENT_DETAIL, payload })
export const setAgentConfigMissing = (payload) => ({ type: SET_AGENT_CONFIG_MISSING, payload })
export const setAgentNotFound = (payload) => ({ type: SET_AGENT_NOT_FOUND, payload })
export const setNode = (payload) => ({ type: SET_NODE, payload })
export const setVariables = (payload) => ({ type: SET_VARIABLES, payload })
export const clearVersionCaches = () => ({ type: CLEAR_VERSION_CACHES })
export const setVersions = (payload) => ({ type: SET_VERSIONS, payload })
export const setVariableVersions = (payload) => ({ type: SET_VARIABLE_VERSIONS, payload })
export const setToolVersions = (payload) => ({ type: SET_TOOL_VERSIONS, payload })
export const setYaml = (payload) => ({ type: SET_YAML, payload })
export const setSelectedAgent = (payload) => ({ type: SET_SELECTED_AGENT, payload })
export const setSelectedNode = (payload) => ({ type: SET_SELECTED_NODE, payload })
export const setDrawerActiveTab = (payload) => ({ type: SET_DRAWER_ACTIVE_TAB, payload })
export const setDrawerVersionOverride = (payload) => ({
  type: SET_DRAWER_VERSION_OVERRIDE,
  payload,
})
export const clearDrawerVersionOverride = (payload) => ({
  type: CLEAR_DRAWER_VERSION_OVERRIDE,
  payload,
})
export const setDrawerHistoryOpen = (payload) => ({ type: SET_DRAWER_HISTORY_OPEN, payload })
export const setChangeRequests = (payload) => ({ type: SET_CHANGE_REQUESTS, payload })
export const upsertChangeRequest = (payload) => ({ type: UPSERT_CHANGE_REQUEST, payload })
export const setTranscriptChangeRequests = (payload) => ({
  type: SET_TRANSCRIPT_CHANGE_REQUESTS,
  payload,
})
export const upsertTranscriptChangeRequest = (payload) => ({
  type: UPSERT_TRANSCRIPT_CHANGE_REQUEST,
  payload,
})
export const setFocusedTranscriptChangeRequest = (payload) => ({
  type: SET_FOCUSED_TRANSCRIPT_CHANGE_REQUEST,
  payload,
})
export const setDrawerContentView = (payload) => ({ type: SET_DRAWER_CONTENT_VIEW, payload })
// Show the content view with one comment focused, so the prompt display can
// scroll to its mark. Atomic for the same reason FOCUS_CHANGE_REQUEST is: a
// setDrawerContentView('content') + setFocusedChangeRequest pair cannot express
// it, because switching to the content view clears the focus on the way.
export const focusChangeRequestInContent = (payload) => ({
  type: FOCUS_CHANGE_REQUEST_IN_CONTENT,
  payload,
})
export const setFocusedChangeRequest = (payload) => ({
  type: SET_FOCUSED_CHANGE_REQUEST,
  payload,
})
export const focusChangeRequest = (payload) => ({ type: FOCUS_CHANGE_REQUEST, payload })
export const setVoices = (payload) => ({ type: SET_VOICES, payload })
export const setChosenVoice = (payload) => ({ type: SET_CHOSEN_VOICE, payload })
export const setSavedVoice = (payload) => ({ type: SET_SAVED_VOICE, payload })
export const setLlmCatalog = (payload) => ({ type: SET_LLM_CATALOG, payload })
export const setLlmSettings = (payload) => ({ type: SET_LLM_SETTINGS, payload })
export const setLlmVersions = (payload) => ({ type: SET_LLM_VERSIONS, payload })
export const setLanguageCatalog = (payload) => ({ type: SET_LANGUAGE_CATALOG, payload })
export const setLanguageSettings = (payload) => ({ type: SET_LANGUAGE_SETTINGS, payload })
export const setLanguageVersions = (payload) => ({ type: SET_LANGUAGE_VERSIONS, payload })
export const setLanguagePreview = (payload) => ({ type: SET_LANGUAGE_PREVIEW, payload })
export const setCallDataDeletion = (payload) => ({ type: SET_CALL_DATA_DELETION, payload })

// Initial state
export const initialState = {
  // AI-agent users for the currently-viewed org. Populated by
  // fetchAiAgentUsers(orgId) — the BE filters by `organizationid` header.
  // `orgId` records which org the items belong to so a stale render after
  // an org switch is easy to detect.
  aiAgentUsers: { items: [], orgId: null },

  // Per-agent full detail (the /config response). Keyed by agentId.
  // Each entry is the AgentDetail shape from pe-api-reference.md.
  detailById: {},

  // True when the agent opened via /config/by-user has no externalized prompt
  // config yet (BE 404) — an expected empty state, not an error.
  agentConfigMissing: false,

  // True when the deep-linked agent isn't reachable by this caller: it doesn't
  // exist, or it belongs to another org. The BE's per-agent ownership guard
  // answers 404 for both (deliberately, so slugs can't be enumerated), and
  // /config/by-user answers 403 for the cross-org case. Also an expected empty
  // state for a stale or hand-edited URL, not an error — distinct from
  // agentConfigMissing, which means "your agent, just not externalized yet".
  agentNotFound: false,

  // Per-node version history (loaded on-demand by the drawer).
  // Keyed by nodeId. Reset on agent change.
  versionsByNodeId: {},

  // Per-variable version history. Keyed by variable name. Reset on agent
  // change. Variables don't carry varsSignature staleness like prompts do —
  // BE always reports isCurrentSignature: true for them.
  versionsByVariableName: {},

  // Per-tool version history. Keyed by tool name (the BE target_id, which is
  // node.config.name for tool nodes). Reset on agent change.
  versionsByToolName: {},

  // Raw prompt_config.yaml text per agent for the YAML modal.
  yamlByAgentId: {},

  // Prompt change requests ("comments") for the selected agent, newest-first
  // as returned by the BE. `agentId` records which agent the items belong to
  // (same staleness pattern as aiAgentUsers). Reset on agent change.
  changeRequests: { items: [], agentId: null },

  // Transcript change requests ("comments") for the call open in the Call
  // Explorer. Keyed by `callId` instead of agentId because the Call Explorer
  // never sets `selectedAgentId` — reusing the agent-scoped slice above would
  // trip its staleness guard and silently drop every transcript row.
  transcriptChangeRequests: { items: [], callId: null, focusedId: null },

  // TTS voice catalog for the selected agent (Overview-tab picker). The whole
  // catalog is loaded up front (a few hundred voices) and filtered client-side.
  // `agentId` records which agent the items belong to (same staleness pattern
  // as aiAgentUsers). Reset on agent change.
  voices: { items: [], agentId: null },

  // Voice selection for the picker. `chosenId` is the in-panel (unsaved) pick,
  // `savedId` mirrors the BE-persisted voice — committed on PATCH success and
  // seeded from the catalog response's selectedVoiceId on load. Reset on
  // agent change.
  voiceSelection: { chosenId: null, savedId: null },

  // LLM model catalog (GET /prompts/llm/catalog). Not agent-scoped — the list
  // only changes by BE deploy — so it loads once per session and survives
  // agent switches. `loaded` distinguishes "not fetched yet" from an empty
  // catalog so the panel's fetch guard doesn't loop.
  llmCatalog: { models: [], loaded: false },

  // Per-target LLM settings (GET /prompts/llm): { baseline, override,
  // effective, hasOverride }, keyed by node id with '__agent__' for the
  // agent-level target. The panel refetches on every mount (no staleness
  // guard needed — an agent-level save changes every node's effective
  // values). Reset on agent change.
  llmSettingsByNodeId: {},

  // Per-target LLM settings version history. Same keying; reset on agent
  // change.
  llmVersionsByNodeId: {},

  // Language catalog (GET /prompts/languages/catalog): server-owned, ordered
  // for display, grows by BE deploy. Session-scoped like llmCatalog; `loaded`
  // keeps the panel's fetch guard from looping on an empty list. There is no
  // ASR-model catalog: the model is derived from the language count, so the BE
  // dropped `sttPresets` on 2026-09-18.
  languageCatalog: { languages: [], loaded: false },

  // GET /prompts/languages for the selected agent: effective set, start
  // language, wording, override state and versions. No provider state — a save
  // stopped writing to Cartesia on 2026-09-18, so there is none to report. The
  // whole payload is kept so the panel reads it from one place. Reset on agent
  // change.
  languageSettings: null,

  // GET /prompts/languages/versions. Oldest first, despite what the BE's own
  // endpoint table says — the serializer reverses the newest-first query. The
  // panel sorts by `version` itself rather than depend on either claim.
  // Reset on agent change.
  languageVersions: [],

  // GET /prompts/languages/preview: the generated LANGUAGE text for a set the
  // operator has picked but not saved, as { key, languageInstructions }. Keyed
  // by the set it was rendered for (languagePreviewKey), so an earlier
  // in-flight preview can never be read as the current selection's text.
  // Reset on agent change.
  languagePreview: null,

  // Whether call data is deleted at the voice provider, keyed by the
  // voice_agents master-row id each flag was read for. The panel reads its own
  // agent's key only, so a value fetched for the previously-viewed agent can
  // never be shown — or flipped — against the current one, and a slow read
  // landing after the user has moved on writes a key nobody is looking at
  // instead of overwriting the agent now on screen.
  callDataDeletionById: {},

  // UI selection
  selectedAgentId: null,
  selectedNodeId: null,

  // Drawer-scoped UI state. Reset inline on agent/node change (see the
  // SET_SELECTED_AGENT / SET_SELECTED_NODE cases).
  drawer: {
    activeTab: 'prompt', // 'prompt' | 'model' | 'handoffs' | 'tools' | 'variables'
    contentView: 'content', // 'content' | 'comments' — per-tab sub-view toggle
    focusedRequestId: null, // change request to scroll/highlight in the comments list
    versionOverrideByNodeId: {}, // { [nodeId]: versionNumber } — peek state
    historyOpen: false,
  },

  // Per-resource loading flags (matches the prompts slice convention)
  loading: {
    aiAgentUsers: false,
    agentDetail: false,
    versions: false,
    varVersions: false,
    toolVersions: false,
    yaml: false,
    savePrompt: false,
    revertPrompt: false,
    resetPrompt: false,
    declareVariable: false,
    saveVariable: false,
    revertVariable: false,
    resetVariable: false,
    saveTool: false,
    revertTool: false,
    resetTool: false,
    exportOverrides: false,
    importOverridesDryRun: false,
    importOverridesApply: false,
    reseedConfig: false,
    changeRequests: false,
    createChangeRequest: false,
    resolveChangeRequest: false,
    editChangeRequest: false,
    transcriptChangeRequests: false,
    voices: false,
    sayVoice: false,
    saveVoice: false,
    llmCatalog: false,
    llmSettings: false,
    llmVersions: false,
    saveLlm: false,
    revertLlm: false,
    resetLlm: false,
    languageCatalog: false,
    saveLanguages: false,
    revertLanguages: false,
    resetLanguages: false,
    callDataDeletion: false,
    saveCallDataDeletion: false,
  },

  // Per-resource error messages (null when no error)
  error: {
    aiAgentUsers: null,
    agentDetail: null,
    versions: null,
    varVersions: null,
    toolVersions: null,
    yaml: null,
    savePrompt: null,
    revertPrompt: null,
    resetPrompt: null,
    declareVariable: null,
    saveVariable: null,
    revertVariable: null,
    resetVariable: null,
    saveTool: null,
    revertTool: null,
    resetTool: null,
    exportOverrides: null,
    importOverrides: null,
    reseedConfig: null,
    changeRequests: null,
    createChangeRequest: null,
    resolveChangeRequest: null,
    editChangeRequest: null,
    transcriptChangeRequests: null,
    // Voice errors are objects ({ kind, detail }) so the panel can branch on
    // kind — 'retryable' (upstream hiccup, show Try again), 'notConfigured'
    // (no voice provider for this tenant), 'unavailable' (unknown agent),
    // 'validation' (bad say payload, shown inline).
    voices: null,
    sayVoice: null,
    llmCatalog: null,
    llmSettings: null,
    llmVersions: null,
    saveLlm: null,
    revertLlm: null,
    resetLlm: null,
    // languageSettings is an object: { kind: 'unavailable' } (404 — the BE
    // doesn't serve the routes for this agent yet; the panel hides) or
    // { kind: 'retryable' } (the panel offers a retry). The write keys hold
    // { kind, message, items[] } from languageErrorDetail so lists can render.
    languageCatalog: null,
    languageSettings: null,
    saveLanguages: null,
    revertLanguages: null,
    resetLanguages: null,
    // Also an object ({ kind, detail, voiceAgentId }): 'unavailable' (no such
    // agent, or the caller is not a Balto admin) hides the panel; 'retryable'
    // offers a retry. Carries the agent it was read for so a previous agent's
    // failure can't paint over the one now on screen.
    callDataDeletion: null,
    saveCallDataDeletion: null,
  },
}

// Case handlers
//
// The branch-heavy cases live here rather than inline so the reducer's switch
// stays a flat dispatch. Each mutates the immer draft in place.

// BE lists change-requests newest-first; a just-created row belongs at the top,
// while a create/resolve response for an existing row replaces it in place.
const upsertRequestItem = (items, request) => {
  const index = items.findIndex((item) => item.id === request.id)
  if (index === -1) {
    items.unshift(request)
  } else {
    items[index] = request
  }
}

const applyNode = (newState, payload) => {
  const detail = newState.detailById[payload.agentId]
  if (detail && Array.isArray(detail.nodes)) {
    const index = detail.nodes.findIndex((node) => node.id === payload.nodeId)
    if (index !== -1) {
      detail.nodes[index] = payload.node
    }
  }
}

const applySelectedAgent = (newState, state, payload) => {
  newState.selectedAgentId = payload
  // Selecting a different agent invalidates the per-node versions cache
  // and resets drawer state. Detail cache survives so back/forward is fast.
  if (payload !== state.selectedAgentId) {
    newState.versionsByNodeId = {}
    newState.versionsByVariableName = {}
    newState.versionsByToolName = {}
    newState.drawer = { ...initialState.drawer }
    newState.selectedNodeId = null
    newState.changeRequests = { ...initialState.changeRequests }
    newState.voices = { ...initialState.voices }
    newState.voiceSelection = { ...initialState.voiceSelection }
    newState.llmSettingsByNodeId = {}
    newState.llmVersionsByNodeId = {}
    newState.languageSettings = null
    newState.languageVersions = []
    newState.languagePreview = null
    // The per-target LLM errors are agent-scoped, and the panel renders its
    // error state from `!settings && error.llmSettings` — both true right
    // after a switch — so a previous agent's failure would flash on the new
    // agent's first paint. The catalog error is session-scoped, so it stays.
    newState.error = {
      ...newState.error,
      llmSettings: null,
      llmVersions: null,
      saveLlm: null,
      revertLlm: null,
      resetLlm: null,
      languageSettings: null,
      saveLanguages: null,
      revertLanguages: null,
      resetLanguages: null,
    }
  }
}

const applyVoices = (newState, payload) => {
  // Staleness guard: a catalog fetch for a previously-selected agent can
  // resolve after the user switches — don't overwrite the new agent's list.
  if (payload.agentId !== newState.selectedAgentId) {
    return
  }
  newState.voices = {
    agentId: payload.agentId,
    items: Array.isArray(payload.items) ? payload.items : [],
  }
  // Seed the picker from the persisted selection (null when none saved). An
  // id missing from the catalog renders as "nothing preselected" — the rows
  // just won't match it.
  const savedId = payload.selectedVoiceId ?? null
  newState.voiceSelection = { chosenId: savedId, savedId }
}

const applyChangeRequests = (newState, payload) => {
  // Staleness guard: a fetch for a previously-selected agent can resolve
  // after the user switches — don't let it overwrite the new agent's list.
  if (payload.agentId !== newState.selectedAgentId) {
    return
  }
  newState.changeRequests = {
    agentId: payload.agentId,
    items: Array.isArray(payload.items) ? payload.items : [],
  }
}

const applyUpsertChangeRequest = (newState, payload) => {
  // Same staleness guard for create/resolve responses landing after an
  // agent switch — keep foreign-agent items out of the list.
  if (payload.agentId && payload.agentId !== newState.selectedAgentId) {
    return
  }
  // POST/PATCH responses carry the same fully-serialized row as the GET
  // routes (names included), so a straight replace is safe.
  upsertRequestItem(newState.changeRequests.items, payload)
}

const applyTranscriptChangeRequests = (newState, payload) => {
  // Call-scoped equivalent of applyChangeRequests. A fetch for a
  // previously-open call can resolve after the user opens another call —
  // the loaded list's own callId is the only reference we have, so a
  // response for a different call still replaces the list but drops the
  // stale focus. Focus survives a same-call refresh.
  const sameCall = newState.transcriptChangeRequests.callId === payload.callId
  newState.transcriptChangeRequests = {
    callId: payload.callId,
    items: Array.isArray(payload.items) ? payload.items : [],
    focusedId: sameCall ? newState.transcriptChangeRequests.focusedId : null,
  }
}

const applyUpsertTranscriptChangeRequest = (newState, payload) => {
  // Keep a create/resolve response for a different call out of the list
  // the user is currently looking at.
  if (payload.callId && payload.callId !== newState.transcriptChangeRequests.callId) {
    return
  }
  upsertRequestItem(newState.transcriptChangeRequests.items, payload)
}

// Staleness guard for the LLM maps: a fetch for a previously-selected agent
// can resolve after the user switches. Unlike the versions caches these are
// keyed by node id alone, and node ids repeat across agents ('__agent__'
// addresses every agent's own defaults), so an unguarded write would show the
// previous agent's settings under the new one.
const isStaleAgentPayload = (newState, payload) => payload.agentId !== newState.selectedAgentId

// Reducer
export default function voiceAgentsReducer(state = initialState, action) {
  return produce(state, (newState) => {
    const { payload } = action

    switch (action.type) {
      case SET_LOADING:
        newState.loading = { ...newState.loading, ...payload }
        break
      case SET_ERROR:
        newState.error = { ...newState.error, ...payload }
        break
      case SET_AI_AGENT_USERS:
        newState.aiAgentUsers = {
          items: Array.isArray(payload?.items) ? payload.items : [],
          orgId: payload?.orgId ?? null,
        }
        break
      case SET_AGENT_DETAIL:
        newState.detailById[payload.agentId] = payload.detail
        // A real config loaded → clear any "no prompts" empty state.
        newState.agentConfigMissing = false
        newState.agentNotFound = false
        break
      case SET_AGENT_CONFIG_MISSING:
        newState.agentConfigMissing = payload
        break
      case SET_AGENT_NOT_FOUND:
        newState.agentNotFound = payload
        break
      case SET_NODE:
        applyNode(newState, payload)
        break
      case SET_VARIABLES: {
        const detail = newState.detailById[payload.agentId]
        if (detail) {
          detail.variables = payload.variables
        } else {
          newState.detailById[payload.agentId] = { variables: payload.variables }
        }
        break
      }
      case CLEAR_VERSION_CACHES:
        // The active generation changed. Everything keyed to an override
        // belongs to the generation we just left, so drop it rather than let
        // the drawer keep rendering another generation's edits.
        newState.versionsByNodeId = {}
        newState.versionsByVariableName = {}
        newState.versionsByToolName = {}
        newState.llmVersionsByNodeId = {}
        // LLM settings carry hasOverride and the effective values a generation's
        // llm overrides produced — generation-scoped, not agent-scoped.
        newState.llmSettingsByNodeId = {}
        // The peek selection points at a version number that may not exist on
        // the new generation, which would leave the drawer read-only on a
        // version it can never load.
        newState.drawer = { ...newState.drawer, versionOverrideByNodeId: {} }
        break
      case SET_VERSIONS:
        newState.versionsByNodeId[payload.nodeId] = payload.versions
        break
      case SET_VARIABLE_VERSIONS:
        newState.versionsByVariableName[payload.varName] = payload.versions
        break
      case SET_TOOL_VERSIONS:
        newState.versionsByToolName[payload.toolName] = payload.versions
        break
      case SET_YAML:
        newState.yamlByAgentId[payload.agentId] = payload.yaml
        break
      case SET_SELECTED_AGENT:
        applySelectedAgent(newState, state, payload)
        break
      case SET_SELECTED_NODE:
        newState.selectedNodeId = payload
        // Switching node clears in-flight peek + history on the previous node.
        newState.drawer.versionOverrideByNodeId = {}
        newState.drawer.historyOpen = false
        newState.drawer.activeTab = 'prompt'
        newState.drawer.contentView = 'content'
        newState.drawer.focusedRequestId = null
        break
      case SET_DRAWER_ACTIVE_TAB:
        newState.drawer.activeTab = payload
        newState.drawer.contentView = 'content'
        newState.drawer.focusedRequestId = null
        break
      case SET_DRAWER_VERSION_OVERRIDE:
        newState.drawer.versionOverrideByNodeId[payload.nodeId] = payload.version
        break
      case CLEAR_DRAWER_VERSION_OVERRIDE:
        delete newState.drawer.versionOverrideByNodeId[payload]
        break
      case SET_DRAWER_HISTORY_OPEN:
        newState.drawer.historyOpen = payload
        break
      case SET_CHANGE_REQUESTS:
        applyChangeRequests(newState, payload)
        break
      case UPSERT_CHANGE_REQUEST:
        applyUpsertChangeRequest(newState, payload)
        break
      case SET_TRANSCRIPT_CHANGE_REQUESTS:
        applyTranscriptChangeRequests(newState, payload)
        break
      case SET_FOCUSED_TRANSCRIPT_CHANGE_REQUEST:
        newState.transcriptChangeRequests.focusedId = payload
        break
      case UPSERT_TRANSCRIPT_CHANGE_REQUEST:
        applyUpsertTranscriptChangeRequest(newState, payload)
        break
      case SET_DRAWER_CONTENT_VIEW:
        newState.drawer.contentView = payload
        if (payload === 'content') {
          newState.drawer.focusedRequestId = null
        }
        break
      case SET_VOICES:
        applyVoices(newState, payload)
        break
      case SET_CHOSEN_VOICE:
        newState.voiceSelection.chosenId = payload
        break
      case SET_SAVED_VOICE:
        // Committing also aligns chosenId so the panel's dirty state clears.
        newState.voiceSelection = { chosenId: payload, savedId: payload }
        break
      case SET_LLM_CATALOG:
        newState.llmCatalog = { models: Array.isArray(payload) ? payload : [], loaded: true }
        break
      case SET_LLM_SETTINGS:
        if (!isStaleAgentPayload(newState, payload)) {
          newState.llmSettingsByNodeId[payload.nodeId] = payload.settings
        }
        break
      case SET_LLM_VERSIONS:
        if (!isStaleAgentPayload(newState, payload)) {
          newState.llmVersionsByNodeId[payload.nodeId] = payload.versions
        }
        break
      // Both lists are normalised here rather than in the thunk: the panel maps
      // over them unguarded, so this is the one funnel that has to hold.
      case SET_LANGUAGE_CATALOG:
        newState.languageCatalog = {
          languages: Array.isArray(payload?.languages) ? payload.languages : [],
          loaded: true,
        }
        break
      case SET_LANGUAGE_SETTINGS:
        if (!isStaleAgentPayload(newState, payload)) {
          newState.languageSettings = payload.settings
        }
        break
      case SET_LANGUAGE_VERSIONS:
        if (!isStaleAgentPayload(newState, payload)) {
          newState.languageVersions = Array.isArray(payload.versions) ? payload.versions : []
        }
        break
      case SET_LANGUAGE_PREVIEW:
        if (!isStaleAgentPayload(newState, payload)) {
          newState.languagePreview = {
            key: payload.key,
            languageInstructions: payload.languageInstructions,
          }
        }
        break
      case SET_CALL_DATA_DELETION:
        // Keyed rather than a single slot: two reads can be in flight after a
        // fast agent switch, and the later-resolving one must not clobber the
        // agent now on screen. Can't reuse isStaleAgentPayload here —
        // selectedAgentId is the Studio agent id, a different id space from
        // the voice_agents row id these routes take.
        newState.callDataDeletionById[payload.voiceAgentId] = payload.enabled
        break
      case SET_FOCUSED_CHANGE_REQUEST:
        // Lightweight highlight control (click-away clear) — unlike
        // FOCUS_CHANGE_REQUEST this touches nothing but the highlight.
        newState.drawer.focusedRequestId = payload
        break
      case FOCUS_CHANGE_REQUEST_IN_CONTENT:
        newState.drawer.contentView = 'content'
        newState.drawer.focusedRequestId = payload ?? null
        break
      case FOCUS_CHANGE_REQUEST:
        // Atomic navigation from outside the drawer (Change Requests page,
        // copy-link deep links). A setSelectedNode + setDrawerActiveTab pair
        // can't express this: both of those reset contentView/focusedRequestId.
        newState.selectedNodeId = payload.nodeId
        newState.drawer.activeTab = payload.tabKey
        newState.drawer.contentView = 'comments'
        newState.drawer.focusedRequestId = payload.requestId ?? null
        newState.drawer.versionOverrideByNodeId = {}
        newState.drawer.historyOpen = false
        break
      default:
        break
    }
  })
}
